import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  fetchUserCategories,
  fetchUserCategoryById,
  createUserCategory,
  updateUserCategory,
  deleteUserCategory,
  fetchUserSubcategories,
  fetchUserSubcategoryById,
  createUserSubcategory,
  updateUserSubcategory,
  deleteUserSubcategory,
  fetchUserTypes,
} from "../services/userCategoriesService";
import { resolveApiErrorMessage } from "../utils/resolveApiErrorMessage";

const USER_CATEGORIES_QK = ["user-categories"];
const USER_SUBCATEGORIES_QK = ["user-subcategories"];
const USER_TYPES_QK = ["user-types"];

const STALE_TIME = 2 * 60 * 1000;

const baseQueryOptions = {
  staleTime: STALE_TIME,
  refetchOnWindowFocus: false,
};

export const useUserCategories = (options = {}) =>
  useQuery({
    queryKey: USER_CATEGORIES_QK,
    queryFn: fetchUserCategories,
    ...baseQueryOptions,
    ...options,
  });

export const useUserCategory = (id, options = {}) =>
  useQuery({
    queryKey: [...USER_CATEGORIES_QK, id],
    queryFn: () => fetchUserCategoryById(id),
    enabled: !!id,
    ...baseQueryOptions,
    ...options,
  });

export const useCreateUserCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createUserCategory,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USER_CATEGORIES_QK });
      toast.success("Catégorie bien ajoutée", { autoClose: 3000 });
    },
    onError: (err) =>
      toast.error(resolveApiErrorMessage(err, "Catégorie non ajoutée"), {
        autoClose: 5000,
      }),
  });
};

export const useUpdateUserCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => updateUserCategory(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USER_CATEGORIES_QK });
      toast.success("Catégorie mise à jour avec succès", { autoClose: 3000 });
    },
    onError: (err) =>
      toast.error(resolveApiErrorMessage(err, "Catégorie non mise à jour"), {
        autoClose: 5000,
      }),
  });
};

export const useDeleteUserCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteUserCategory,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USER_CATEGORIES_QK });
      toast.success("Catégorie supprimée avec succès", { autoClose: 3000 });
    },
    onError: (err) =>
      toast.error(resolveApiErrorMessage(err, "Erreur lors de la suppression"), {
        autoClose: 5000,
      }),
  });
};

/**
 * Institutions (subcategories). Passing `categoryId` scopes the list to a
 * single institution category and keeps its own cache entry.
 */
export const useUserSubcategories = (categoryId, options = {}) =>
  useQuery({
    queryKey: categoryId
      ? [...USER_SUBCATEGORIES_QK, { categoryId }]
      : USER_SUBCATEGORIES_QK,
    queryFn: () => fetchUserSubcategories(categoryId),
    ...baseQueryOptions,
    ...options,
  });

export const useUserSubcategory = (id, options = {}) =>
  useQuery({
    queryKey: [...USER_SUBCATEGORIES_QK, id],
    queryFn: () => fetchUserSubcategoryById(id),
    enabled: !!id,
    ...baseQueryOptions,
    ...options,
  });

export const useCreateUserSubcategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createUserSubcategory,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USER_SUBCATEGORIES_QK });
      // Category rows carry a subcategories count, so they go stale too.
      qc.invalidateQueries({ queryKey: USER_CATEGORIES_QK });
      toast.success("Institution bien ajoutée", { autoClose: 3000 });
    },
    onError: (err) =>
      toast.error(resolveApiErrorMessage(err, "Institution non ajoutée"), {
        autoClose: 5000,
      }),
  });
};

export const useUpdateUserSubcategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => updateUserSubcategory(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USER_SUBCATEGORIES_QK });
      qc.invalidateQueries({ queryKey: USER_CATEGORIES_QK });
      toast.success("Institution mise à jour avec succès", { autoClose: 3000 });
    },
    onError: (err) =>
      toast.error(resolveApiErrorMessage(err, "Institution non mise à jour"), {
        autoClose: 5000,
      }),
  });
};

export const useDeleteUserSubcategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteUserSubcategory,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USER_SUBCATEGORIES_QK });
      qc.invalidateQueries({ queryKey: USER_CATEGORIES_QK });
      toast.success("Institution supprimée avec succès", { autoClose: 3000 });
    },
    onError: (err) =>
      toast.error(resolveApiErrorMessage(err, "Erreur lors de la suppression"), {
        autoClose: 5000,
      }),
  });
};

export const useUserTypes = (options = {}) =>
  useQuery({
    queryKey: USER_TYPES_QK,
    queryFn: fetchUserTypes,
    ...baseQueryOptions,
    ...options,
  });
